import ImageStrategy from "src/api/graphql/__generated__/enums/ImageStrategy";
import VideoStrategy from "src/api/graphql/__generated__/enums/VideoStrategy";
import { enumNever } from "src/shared/helpers/generalHelpers";
import isWebpSupported from "./constants/isWebpSupported";

// You can just paste this over from `aws-backend/src/lambdas/image-optimization-stack/OptimizeImage/handlers/handleV0Cache/types.ts`
type Size = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
type AspectRatio = "1x1" | "3x4" | "4x3" | "9x16" | "5x8" | "2x3" | "3x2" | "5x2";
type Strategy = "cover" | "contain" | "fill" | "inside" | "outside";
type ImageFormat = "webp" | "jpeg" | "png" | "avif" | "auto" | "transparent";
type Direct = "original" | "stable" | "original-video" | "stable-video";
type Auto = "allowStream";

const parseFormat = (format: ImageFormat): string => {
	if (format === "auto") {
		// Both avif and webp are slow on initial optimization
		return "jpeg";
	}
	if (format === "transparent") {
		if (isWebpSupported) {
			return "webp";
		}
		return "png";
	}
	return format;
};

/**
 * Parses a v0 template URL into either an optimization or a pass-through URL. If the URL is not a template URL, it just returns the URL.
 *
 * By default, optimizes for a medium sized image which selects format based on platform.
 *
 * You can also configure "direct" to retrieve some source URL directly.
 */
const parseV0TemplateUrl = ({
	readUrl,
	size = "md",
	aspectRatio = "1x1",
	strategy = "cover",
	format = "auto",
	direct,
	auto,
}: {
	readUrl:
		| {
				v0TemplateUrl?: string | null | undefined;
				url?: string | null | undefined;
				stableUrl?: string | null | undefined;
				videoUrl?: string | null | undefined;
				stableVideoUrl?: string | null | undefined;
				imageStrategy?: ImageStrategy | null | undefined;
				videoStrategy?: VideoStrategy | null | undefined;
		  }
		| null
		| undefined;
} & (
	| {
			size?: Size;
			aspectRatio?: AspectRatio;
			strategy?: Strategy;
			format?: ImageFormat;
			direct?: never;
			auto?: never;
	  }
	| {
			size?: never;
			aspectRatio?: never;
			strategy?: never;
			format?: never;
			direct?: Direct;
			auto?: never;
	  }
	| {
			size?: never;
			aspectRatio?: never;
			strategy?: never;
			format?: never;
			direct?: never;
			auto?: Auto;
	  }
)): string | null => {
	const templateUrl = readUrl?.v0TemplateUrl;
	if (direct) {
		if (!templateUrl) {
			switch (direct) {
				case "original":
					return readUrl?.url ?? null;
				case "stable":
					return readUrl?.stableUrl ?? null;
				case "original-video":
					return readUrl?.videoUrl ?? null;
				case "stable-video":
					return readUrl?.stableVideoUrl ?? null;
				default:
					return enumNever(direct);
			}
		}
		return templateUrl.replace("/TEMPLATE_PLACEHOLDER", `/pass-through/${direct}`);
	} else if (auto) {
		switch (auto) {
			case "allowStream":
				if (!templateUrl) return readUrl?.stableVideoUrl ?? readUrl?.videoUrl ?? null;
				switch (readUrl.videoStrategy) {
					case VideoStrategy.PreferStream:
						return templateUrl.replace(
							"/TEMPLATE_PLACEHOLDER",
							`/stream/original-video/hls.m3u8`,
						);
					case VideoStrategy.Stable:
						return templateUrl.replace(
							"/TEMPLATE_PLACEHOLDER",
							`/pass-through/stable-video`,
						);
					// Don't throw -- we may receive new video strategies
					default:
						return templateUrl.replace(
							"/TEMPLATE_PLACEHOLDER",
							`/stream/original-video/hls.m3u8`,
						);
				}
			default:
				return enumNever(auto);
		}
	} else {
		const parsedFormat = parseFormat(format);
		if (!templateUrl) return readUrl?.stableUrl ?? null;
		return templateUrl.replace(
			"/TEMPLATE_PLACEHOLDER",
			`/optimize/${size}/${aspectRatio}/${strategy}/${parsedFormat}`,
		);
	}
};
export default parseV0TemplateUrl;
